<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("dashboard.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("dashboard.subTitle") }}
      </div>
    </div>
    <div class="bg-white rounded-xl shadow-md p-4" v-if="!isLoading">
      <CustomDropDown
        :title="t('executiveDashboard.selectMonth')"
        :data="monthsByYear"
        dataKey="year"
        dataValue="year"
        subOptionsKey="months"
        subOptionKey="id"
        subOptionValue="month"
        v-model="selectedMonth"
        @update:modelValue="loadInitialData"
      />
    </div>
  </div>
  <div v-if="isLoading" class="flex h-[60vh] justify-center">
    <LoadingIcon :isLoading="isLoading" />
  </div>
  <div class="flex flex-col gap-4" v-if="!isLoading">
    <div class="grid grid-cols-6 gap-3 my-4">
      <InfoBox
        :title="EventState.events[0].getLocalization()"
        :value="eventsGeneralInformation!.numberOfNotStartedEvents"
        :backgroundColor="EventState.events[0].lightColor"
        :textColor="EventState.events[0].color"
      >
        <IconManager
          name="Calendar"
          :style="{ color: EventState.events[0].color }"
        />
      </InfoBox>

      <InfoBox
        :title="EventState.events[1].getLocalization()"
        :value="eventsGeneralInformation!.numberOfInPreparation"
        :backgroundColor="EventState.events[1].lightColor"
        :textColor="EventState.events[1].color"
      >
        <IconManager
          name="Search"
          :style="{ color: EventState.events[1].color }"
        />
      </InfoBox>

      <InfoBox
        :title="EventState.events[2].getLocalization()"
        :value="eventsGeneralInformation!.numberOfPrepared"
        :backgroundColor="EventState.events[2].lightColor"
        :textColor="EventState.events[2].color"
      >
        <IconManager
          name="Clock"
          :style="{ color: EventState.events[2].color }"
        />
      </InfoBox>

      <InfoBox
        :title="EventState.events[3].getLocalization()"
        :value="eventsGeneralInformation!.numberOfInExecution"
        :backgroundColor="EventState.events[3].lightColor"
        :textColor="EventState.events[3].color"
      >
        <IconManager
          name="Play"
          :style="{ color: EventState.events[3].color }"
        />
      </InfoBox>

      <InfoBox
        :title="EventState.events[4].getLocalization()"
        :value="eventsGeneralInformation!.numberOfConcluded"
        :backgroundColor="EventState.events[4].lightColor"
        :textColor="EventState.events[4].color"
      >
        <IconManager
          name="Flag"
          :style="{ color: EventState.events[4].color }"
        />
      </InfoBox>
      <InfoBox
        :title="EventState.events[5].getLocalization()"
        :value="eventsGeneralInformation!.numberOfCanceled"
        :backgroundColor="EventState.events[5].lightColor"
        :textColor="EventState.events[5].color"
      >
        <IconManager
          name="Cancel"
          :style="{ color: EventState.events[5].color }"
        />
      </InfoBox>
    </div>
    <div class="flex flex-row gap-4">
      <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
        <div class="flex flex-row w-full justify-between">
          <span class="font-extrabold">
            {{ t("dashboard.calendar.title") }}
          </span>
          <span class="text-light-gray"
            >{{
              getMonthName(selectedMonth.subOption.id, locale) +
              " " +
              selectedMonth.option.year
            }}
          </span>
          <div class="flex flex-row gap-2">
            <CustomButton @click="nextMonth(-1)">
              <IconManager name="ArrowDown" class="rotate-90"
            /></CustomButton>

            <CustomButton @click="nextMonth(1)">
              <IconManager name="ArrowDown" class="-rotate-90"
            /></CustomButton>
          </div>
        </div>
        <div v-if="isLoadingCharts" class="flex h-[50vh] justify-center">
          <LoadingIcon :isLoading="isLoadingCharts" />
        </div>
        <EventCalendar
          v-if="!isLoadingCharts"
          :events="
            eventCalendarData[eventCalendarData.length - 1].events.map(
              (item) => {
                return {
                  id: item.eventId,
                  title: item.name,
                  start: item.interval.startDate,
                  end: item.interval.endDate,
                  color: item.status.color,
                };
              },
            )
          "
          :callBack="(id: string) => go(RouteName.EventDetailEdit, { id: id })"
          :clickable="true"
        />
      </div>

      <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
        <div class="flex flex-row w-full justify-between">
          <span class="font-extrabold"
            >{{ t("dashboard.barChart.title") }}
          </span>
          <span class="text-light-gray"
            >{{ t("dashboard.barChart.subTitle") }}
          </span>
        </div>
        <div v-if="isLoadingCharts" class="flex h-[50vh] justify-center">
          <LoadingIcon :isLoading="isLoadingCharts" />
        </div>
        <BarChartVertical
          v-if="!isLoadingCharts"
          :categories="
            eventCalendarData
              .slice(0, 6)
              .map((item) => getMonthName(item.month, locale))
          "
          :values="
            eventCalendarData.slice(0, 6).map((item) => item.events.length)
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import { useI18n } from "vue-i18n";
import BarChartVertical from "@/core/components/charts/BarChartVertical.vue";
import EventCalendar from "@/core/components/charts/EventCalendar.vue";
import {
  dashboardRemoteDataSource,
  eventsRemoteDataSource,
} from "@/core/models/global-variables";
import { onMounted, ref } from "vue";
import { EventCalendarModel } from "@/core/models/event-calendar-model";
import CustomDropDown from "@/core/components/CustomDropDown.vue";
import { EventState } from "@/modules/events/models/event-state";
import { EventGeneralInformation } from "@/modules/events/models/event-general-information";
import { useRouter } from "vue-router";
import { RouteName } from "@/core/models/route-name";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import CustomButton from "@/core/components/CustomButton.vue";

const { t } = useI18n();
const { showError } = useToast();
const eventsGeneralInformation = ref<EventGeneralInformation | null>(null);
const eventCalendarData = ref<EventCalendarModel[]>([]);
const isLoading = ref<boolean>(true);
const isLoadingCharts = ref<boolean>(false);
const router = useRouter();

const { locale } = useI18n();
const months = getMonthNames(locale.value);

const monthsByYear = [
  { year: new Date().getFullYear(), months: months },
  { year: new Date().getFullYear() + 1, months: months },
];

const selectedMonth = ref<{
  option: { year: number; months: any[] };
  subOption: { id: number; month: string };
}>({
  option: monthsByYear[0],
  subOption: {
    id: new Date().getMonth(),
    month: months[new Date().getMonth()].month,
  },
});

onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  isLoading.value = true;

  try {
    const startDate = new Date(
      selectedMonth.value.option.year,
      selectedMonth.value.subOption.id,
    );
    const endDate = new Date(
      selectedMonth.value.option.year,
      selectedMonth.value.subOption.id + 1,
      0,
    );

    const [generalInformationRes, calendarDataRes] = await Promise.all([
      eventsRemoteDataSource.getGeneralInformation({
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
      }),
      dashboardRemoteDataSource.getCalendarData({
        startDate: new Date(
          selectedMonth.value.option.year,
          selectedMonth.value.subOption.id - 5,
          1,
        ).getTime(),
        endDate: endDate.getTime(),
      }),
    ]);
    if (!generalInformationRes.isSuccess) {
      showError({
        message: generalInformationRes.detail!,
        title: generalInformationRes.title,
        statusCode: generalInformationRes.statusCode,
      });
    }
    eventsGeneralInformation.value = generalInformationRes.data;

    if (!calendarDataRes.isSuccess) {
      showError({
        message: calendarDataRes.detail!,
        title: calendarDataRes.title,
        statusCode: calendarDataRes.statusCode,
      });
    }
    eventCalendarData.value = calendarDataRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function updateData() {
  isLoadingCharts.value = true;

  try {
    const startDate = new Date(
      selectedMonth.value.option.year,
      selectedMonth.value.subOption.id,
    );
    const endDate = new Date(
      selectedMonth.value.option.year,
      selectedMonth.value.subOption.id + 1,
      0,
    );

    const [generalInformationRes, calendarDataRes] = await Promise.all([
      eventsRemoteDataSource.getGeneralInformation({
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
      }),
      dashboardRemoteDataSource.getCalendarData({
        startDate: new Date(
          selectedMonth.value.option.year,
          selectedMonth.value.subOption.id - 5,
          1,
        ).getTime(),
        endDate: endDate.getTime(),
      }),
    ]);
    if (!generalInformationRes.isSuccess) {
      showError({
        message: generalInformationRes.detail!,
        title: generalInformationRes.title,
        statusCode: generalInformationRes.statusCode,
      });
    }
    eventsGeneralInformation.value = generalInformationRes.data;

    if (!calendarDataRes.isSuccess) {
      showError({
        message: calendarDataRes.detail!,
        title: calendarDataRes.title,
        statusCode: calendarDataRes.statusCode,
      });
    }
    eventCalendarData.value = calendarDataRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoadingCharts.value = false;
  }
}

function getMonthName(month: number, locale: string): string {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  const rawName = formatter.format(new Date(2026, month, 1)); //The year and day don't matter
  return rawName.charAt(0).toUpperCase() + rawName.slice(1);
}

function getMonthNames(locale: string): { id: number; month: string }[] {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const rawName = formatter.format(new Date(currentYear, i, 1));
    const capitalized = rawName.charAt(0).toUpperCase() + rawName.slice(1);
    return { id: i, month: capitalized };
  });
}

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}

async function nextMonth(offset: number) {
  const currentYearIndex = monthsByYear.findIndex(
    (y) => y.year === selectedMonth.value.option.year,
  );
  const currentMonthId = selectedMonth.value.subOption.id;

  let newMonthId = currentMonthId + offset;
  let newYearIndex = currentYearIndex;

  if (newMonthId > 11) {
    newMonthId = 0;
    newYearIndex += 1;
  } else if (newMonthId < 0) {
    newMonthId = 11;
    newYearIndex -= 1;
  }

  // clamp to the years you actually have (current year + next year)
  if (newYearIndex < 0 || newYearIndex >= monthsByYear.length) return;

  const newYearOption = monthsByYear[newYearIndex];
  selectedMonth.value = {
    option: newYearOption,
    subOption: newYearOption.months[newMonthId],
  };

  await updateData();
}
</script>
